import { useState, useCallback } from 'react';
import { Header } from './components/Header';
import { TabNav } from './components/TabNav';
import { FilterBar } from './components/FilterBar';
import { VPPanel } from './components/panels/VPPanel';
import { SDPanel } from './components/panels/SDPanel';
import { ODPanel } from './components/panels/ODPanel';
import { Toast } from './components/ui/Toast';
import { useAppState } from './hooks/useAppState';
import type { Deal, TabState } from './types';
import { ALL_DATA as STATIC_DATA } from './data/deals';
import { fmtG } from './utils/format';
import { getFiltered, buildSCList } from './utils/filter';
export default function App() {
  const {
    state,
    currentTab,
    toast,
    updateTab,
    switchTab,
    drillRegion,
    showToast
  } = useAppState();
  const [allData, setAllData] = useState(STATIC_DATA);
  const [searchRows, setSearchRows] = useState<React.ReactNode>(undefined);

  // Replace global data on CSV upload
  void allData; // referenced via imports in filter.ts which reads ALL_DATA directly

  function handleUpload(data: Deal[]) {
    setAllData(data);
    showToast(`Loaded ${data.length} records`);
  }
  function handleSearch(q: string) {
    if (!q.trim()) {
      setSearchRows(undefined);
      return;
    }
    const ql = q.toLowerCase();
    const hits = STATIC_DATA.filter(d => d.account.toLowerCase().includes(ql) || d.sc.toLowerCase().includes(ql) || d.id.toLowerCase().includes(ql));
    switchTab('sd');
    if (hits.length) {
      const rows = hits.map(d => <tr key={d.id} data-id="element-0">
          <td data-id="element-1"><span style={{
            fontSize: 9,
            color: '#8892a0'
          }} data-id="element-2">{d.id}</span></td>
          <td title={d.account} data-id="element-3">{d.account.substring(0, 24)}</td>
          <td title={d.sc} data-id="element-4">{d.sc.split(' ').slice(0, 2).join(' ')}</td>
          <td data-id="element-5"><span className="badge b-blue" style={{
            fontSize: 8
          }} data-id="element-6">{d.offering.replace('Engagement', 'Eng.').replace('Support', 'Sup.')}</span></td>
          <td className={d.age > 90 ? 'c-red' : d.age > 45 ? 'c-gold' : ''} data-id="element-7">{d.age}d</td>
          <td data-id="element-8">{fmtG(d.gnarr)}</td>
          <td data-id="element-9">{d.close_q}</td>
          <td data-id="element-10"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-11">{d.region}</span></td>
          <td data-id="element-12"><span className="badge b-gray" style={{
            fontSize: 8
          }} data-id="element-13">{d.sub_region}</span></td>
        </tr>);
      setSearchRows(rows);
      showToast(`Found ${hits.length} result${hits.length !== 1 ? 's' : ''} for "${q}"`);
    } else {
      setSearchRows(undefined);
      showToast(`No results for "${q}"`);
    }
  }
  const handleDownload = useCallback(() => {
    let rows: string[][];
    let filename: string;
    if (currentTab === 'od') {
      const scData = buildSCList(state.od.r, state.od.sub, state.od);
      rows = [['SC Name', 'In-Progress', 'Avg Age (d)', 'Median (d)', 'Book GNARR', 'Aged >90', 'Risk GNARR', 'Closed']];
      scData.forEach(s => rows.push([s.n, String(s.ip), String(s.avgAge), String(s.med), s.gnarr.toFixed(0), String(s.aged), s.riskGnarr.toFixed(0), String(s.closed)]));
      filename = 'ondemand_workload.csv';
    } else {
      const filtered = getFiltered(currentTab === 'vp' ? 'vp' : 'sd', currentTab === 'vp' ? state.vp : state.sd);
      rows = [['DR ID', 'Account', 'SC', 'Status', 'Age (d)', 'GNARR', 'Close Quarter', 'Region', 'Sub-region', 'Offering']];
      filtered.forEach(d => rows.push([d.id, d.account, d.sc, d.status, String(d.age), d.gnarr.toFixed(0), d.close_q, d.region, d.sub_region, d.offering]));
      filename = currentTab + '_data.csv';
    }
    const csv = rows.map(r => r.map(v => '"' + String(v).replace(/"/g, '""') + '"').join(',')).join('\n');
    const a = document.createElement('a');
    a.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    a.download = filename;
    a.click();
    showToast('Downloaded ' + filename);
  }, [currentTab, state, showToast]);
  return <div className="min-h-screen" data-id="element-14">
      <Header onSearch={handleSearch} onUpload={handleUpload} onDownload={handleDownload} currentTab={currentTab} data-id="element-15" />
      <TabNav currentTab={currentTab} onSwitch={tab => {
      setSearchRows(undefined);
      switchTab(tab);
    }} data-id="element-16" />

      {currentTab === 'vp' && <>
          <FilterBar tab="vp" state={state.vp} onUpdate={u => updateTab('vp', u as Partial<TabState>)} data-id="element-17" />
          <VPPanel state={state.vp} onDrillRegion={drillRegion} data-id="element-18" />
        </>}
      {currentTab === 'sd' && <>
          <FilterBar tab="sd" state={state.sd} onUpdate={u => updateTab('sd', u as Partial<TabState>)} data-id="element-19" />
          <SDPanel state={state.sd} searchRows={searchRows} data-id="element-20" />
        </>}
      {currentTab === 'od' && <>
          <FilterBar tab="od" state={state.od} onUpdate={u => updateTab('od', u as Partial<TabState>)} data-id="element-21" />
          <ODPanel state={state.od} data-id="element-22" />
        </>}

      <Toast message={toast} data-id="element-23" />
    </div>;
}